import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';

import { Flex } from '@chakra-ui/react';

import { Icon, Button, Heading } from 'components';
import colors from 'styles/themes/wikifactory/styles/colors';
import Tooltip from 'components/Tooltip';

import SupportingFilesSection from 'apps/rfq/views/supportingFilesSection';

import {
  fileFormatsToReadableText,
  getMessageFromRequirement,
} from 'apps/rfq/fileRequirementsUtils';

import useFileInput from 'libs/useFileInput';

import {
  MultiIconWrapper,
  SuperscriptIcon,
  RequireSupportingFilesTitle,
  CenteredParagraph,
  RequireSupportingFilesIconWrapper,
  FooterText,
} from './supportingFiles.styled';
import {
  InfoIcon,
  SubmitWrapper,
} from '../../../jobSpec/views/jobSpecDetailsForm.styled';

const SupportingFiles = observer(({ rfqManager }) => {
  const supportingFiles =
    rfqManager.rfqApp?.jobSpec?.build?.supportingFiles?.apps;
  const { supportingFilesBeingAdded } = rfqManager.rfqApp?.jobSpec?.build;

  const handleAddSupportingFiles = fileList => {
    if (fileList.length > 0) rfqManager.AddLocalSupportingFiles(fileList);
  };

  const handleRemoveSupportingFile = supportingFile => {
    rfqManager.RemoveLocalSupportingFile(supportingFile.id);
  };

  const { fileRequirements, areFileRequirementsValidatedByServer } =
    rfqManager.rfqApp;
  let supportingFilesRequirements = [];
  if (fileRequirements) {
    supportingFilesRequirements = fileRequirements.filter(
      fileRequirement => fileRequirement.type === 'SUPPORTING_FILE',
    );
  }

  const areThereRequirements = supportingFilesRequirements.length > 0;
  const areThereSupportingFiles = supportingFiles && supportingFiles.length > 0;

  return (
    <Flex marginTop='3rem' direction='column'>
      {areThereRequirements && !areThereSupportingFiles ? (
        <RequiredSupportingFilesSection
          supportingFilesRequirements={supportingFilesRequirements}
          onAddSupportingFiles={handleAddSupportingFiles}
        />
      ) : (
        <SupportingFilesSection
          supportingFiles={supportingFiles}
          supportingFilesBeingAdded={supportingFilesBeingAdded}
          supportingFilesRequirements={supportingFilesRequirements}
          areFileRequirementsValidatedByServer={
            areFileRequirementsValidatedByServer
          }
          onAddSupportingFiles={handleAddSupportingFiles}
          onRemoveSupportingFile={handleRemoveSupportingFile}
        />
      )}
    </Flex>
  );
});

SupportingFiles.propTypes = {
  rfqManager: PropTypes.object,
};

const RequiredSupportingFilesSection = observer(props => {
  const {
    dataTestIdPrefix = '',
    supportingFilesRequirements,
    onAddSupportingFiles,
  } = props;
  // Let's have this UI only show the first requirement even if they are multiple for clarity.
  const supportingFileRequirement = supportingFilesRequirements[0];
  const message = getMessageFromRequirement(supportingFileRequirement, false);
  const formatsText = `(${fileFormatsToReadableText(
    supportingFileRequirement.formats,
  )})`;

  const selectFiles = useFileInput(onAddSupportingFiles, {
    accept: supportingFileRequirement.formats.join(','),
  });

  return (
    <>
      <Tooltip
        delayShow={200}
        place='bottom'
        id='requiredSupportingFilesSectionTooltip'
      />
      <Heading level='2' size='large' margin='0 0 0.75rem 0'>
        <Trans>Supporting Files</Trans>
        <InfoIcon
          name='info'
          size='default'
          data-for='requiredSupportingFilesSectionTooltip'
          data-tip='Supporting files are required in order to provide a quote for this part.'
        />
      </Heading>
      <RequireSupportingFilesIconWrapper>
        <MultiIconWrapper>
          <Icon name='cube' size='xlarge' color={colors.wfBlack} />
          <SuperscriptIcon
            name='warning_circle'
            size='xmedium'
            viewBox='-1 -1 24 24'
            color={colors.wfOrange}
          />
        </MultiIconWrapper>
      </RequireSupportingFilesIconWrapper>
      <RequireSupportingFilesTitle>
        <Trans>2D Drawing required</Trans>
      </RequireSupportingFilesTitle>
      <CenteredParagraph>
        <Trans>{message}</Trans>
      </CenteredParagraph>
      <FooterText weight='regular' variant='minimal' size='small'>
        <Trans>{formatsText}</Trans>
      </FooterText>
      <SubmitWrapper>
        <div>
          <Button
            text={<Trans>Upload</Trans>}
            icon='upload'
            outline
            iconPosition='left'
            data-testid={`${dataTestIdPrefix}required-upload`}
            onClick={selectFiles}
          />
        </div>
      </SubmitWrapper>
    </>
  );
});

RequiredSupportingFilesSection.propTypes = {
  supportingFilesRequirements: PropTypes.array,
  onAddSupportingFiles: PropTypes.func,
};

export default withNamespaces()(SupportingFiles);
